import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { allowedVideoMimeTypes, limits, CHROME_STORE_URL } from "@veyso/core";
import { AppShell } from "../components/AppShell.js";
import { NewProjectCrumb } from "../components/NewProjectCrumb.js";
import { audiencePresets, tonePresets, videoPurposes } from "@veyso/core";
import { ErrorNote, Field, PresetChips, Spinner } from "../components/ui.js";
import { projectNameFromFile } from "../lib/project-name.js";
import { armRecorder, useRecorderExtension } from "../lib/extension.js";
import { canRecordInPage } from "../lib/pageRecorder.js";
import { RecordedPanel, RecordingPanel, usePageRecording } from "../components/PageRecorder.js";
import { ApiError, api } from "../api.js";
import { canStartProject } from "../lib/canStartProject.js";
import { startUpload } from "../lib/uploadActivity.js";
import { gapSentence, type ContentProvider } from "../lib/providers.js";
import { capture } from "../lib/posthog.js";

/**
 * These strings are dropped straight into the script prompt, so they are written
 * as instructions to a writer rather than as UI labels.
 */
const MAX_MB = Math.round(limits.maxUploadBytes / (1024 * 1024));

/**
 * Create and upload in one screen. The uploaded file is always the BODY — HEAD
 * and END are decided later in the editor — so this page asks for the recording
 * and gets out of the way. The name comes off the file; tone and audience are
 * a click each and already answered.
 */
export const NewProject: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Started on the first of each rather than on nothing. Every project wants a
  // tone and an audience — the writer uses them whether or not they were
  // answered — so leaving them blank only hides which two it fell back to.
  // Still clearable: clicking the chosen chip clears it.
  const [tone, setTone] = useState<string>(tonePresets[0]);
  const [audience, setAudience] = useState<string>(audiencePresets[0]);
  // Held as the id, shown as the label: rewording a chip should not orphan
  // every project that chose it.
  const [purpose, setPurpose] = useState<string>(videoPurposes[0].id);
  const chosenPurpose = videoPurposes.find((entry) => entry.id === purpose) ?? videoPurposes[0];
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Turned away for want of credit, which is not an error.
   *
   * Held apart from `error` because it is answered differently: an error is
   * something to read and retry, this is a thing to go and do. It used to be
   * reachable only after the project row had been created and the upload
   * refused, which left an empty project behind.
   */
  const [needsCredit, setNeedsCredit] = useState<string | null>(null);

  /**
   * Asked on arrival, so the form is never shown to somebody who cannot use it.
   *
   * The refusal used to come from the server after the form was filled in and
   * submitted, which meant choosing a file, a tone, an audience and a purpose
   * before being told none of it could happen. The answer is knowable the
   * moment this screen opens.
   *
   * There are six ways into this route and one of them is a redirect from
   * onboarding, so the guard is here rather than on the buttons — a check on
   * each entry point is a check somebody forgets to add to the seventh.
   *
   * `null` means not yet known, and the screen renders nothing while it is:
   * showing the form and then replacing it a moment later is worse than a
   * beat of nothing, because somebody will have started typing into it.
   */
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .billing()
      .then((view) => {
        if (!cancelled) setAllowed(canStartProject(view));
      })
      .catch(() => {
        // A check that could not run is not a refusal. The server gates this
        // as well — `POST /api/projects` asks the same question — so letting
        // somebody through on a failed fetch costs them a round trip and
        // nothing else, where refusing them would be locking out an account
        // that may be perfectly fine.
        if (!cancelled) setAllowed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const [arming, setArming] = useState(false);
  const [gaps, setGaps] = useState<ContentProvider[]>([]);
  /**
   * Whether the models run on this upload.
   *
   * Off, the recording becomes a watchable video straight away — title card,
   * footage, end card, its own sound — and nothing is sent to a provider.
   * Narration is then AI Rewrite's job, which is where someone asks for it
   * on purpose rather than getting it because they uploaded a file.
   */
  const [narrate, setNarrate] = useState(true);

  const { available } = useRecorderExtension();

  /**
   * Recording from this page, with nothing installed. No clicks, so the zooms
   * have less to go on — which is why the extension stays the first choice
   * wherever it can be had.
   */
  const inPage = useMemo(canRecordInPage, []);
  // The extension only exists for Chrome and its relatives; elsewhere offering
  // to add it is offering something that cannot be done.
  const chromium = useMemo(
    () => typeof navigator !== "undefined" && /Chrome\/|Chromium\//.test(navigator.userAgent),
    [],
  );
  const showExtension = available || chromium || !inPage;
  const [withMic, setWithMic] = useState(false);
  const pageRecording = usePageRecording(setError);
  const recorded = pageRecording.phase.kind === "recorded" ? pageRecording.phase : null;

  // A finished recording is a chosen file, checked the same way as a dropped one.
  useEffect(() => {
    if (recorded) chooseFile(recorded.outcome.file);
  }, [recorded?.outcome.file]);

  const busyRecording = pageRecording.phase.kind === "recording" || pageRecording.phase.kind === "starting";

  const recordAgain = () => {
    setFile(null);
    pageRecording.reset();
  };

  // Asked before the upload rather than after. The API refuses this too, but by
  // then a long recording has already been sent, and being refused at the end
  // of an upload reads as the upload having failed.
  useEffect(() => {
    let cancelled = false;
    void api
      .ready()
      .then((result) => !cancelled && setGaps(result.unconfigured))
      // A failed probe must not strand someone who is correctly configured;
      // the server is the real gate.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const chooseFile = (candidate: File | undefined) => {
    if (!candidate) return;
    if (!(allowedVideoMimeTypes as readonly string[]).includes(candidate.type)) {
      setError(`${candidate.type || "That file type"} is not supported. Use MP4, MOV, WebM or MKV.`);
      return;
    }
    if (candidate.size > limits.maxUploadBytes) {
      setError(`That file is ${Math.round(candidate.size / (1024 * 1024))}MB — the limit is ${MAX_MB}MB.`);
      return;
    }
    setError(null);
    setFile(candidate);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file || progress !== null) return;

    setError(null);
    setProgress(0);
    try {
      const project = await api.createProject({
        // Named after the recording rather than asked for. Changing it is a
        // rename in the editor, which is where someone who cares about the
        // name already is.
        name: projectNameFromFile(file.name),
        tone: tone.trim() || undefined,
        audience: audience.trim() || undefined,
        purpose,
      });
      capture("project_created", {
        project_id: project.id,
        creation_method: recorded && recorded.outcome.file === file ? "page_recorder" : "upload",
        narration_enabled: narrate,
        purpose,
      });
      // Started, not awaited, and the screen changes immediately. A recording
      // is hundreds of megabytes; holding this form open for the length of it
      // means minutes spent looking at a finished form with a small bar under
      // it, while the project it belongs to is somewhere the user cannot see.
      // The upload outlives this component (lib/uploadActivity) and the
      // project's own screen shows it as the first step of the pipeline, which
      // is what it is.
      void startUpload(project.id, file, narrate);
      navigate(`/projects/${project.id}`);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "no_credit") {
        setNeedsCredit(cause.message);
        setProgress(null);
        return;
      }
      // Only reached if the project could not be created. Once the upload has
      // started, its failures belong to the screen now showing it.
      setError((cause as Error).message);
      setProgress(null);
    }
  };

  /**
   * Creates the project, then points the extension at it.
   *
   * Chrome will not let a page start a tab capture, so this cannot press record
   * for you — it hands the extension a target and sends you to the project,
   * which waits for the recording to arrive.
   */
  const recordWithExtension = async () => {
    if (arming) return;
    setError(null);
    setArming(true);

    try {
      const project = await api.createProject({
        name: "Screen recording",
        tone: tone.trim() || undefined,
        audience: audience.trim() || undefined,
        purpose,
      });

      const armed = await armRecorder(project.id, "Screen recording");
      if (!armed) throw new Error("The recorder extension did not respond. Reload the page and try again.");

      capture("project_created", {
        project_id: project.id,
        creation_method: "recorder_extension",
        narration_enabled: true,
        purpose,
      });
      navigate(`/projects/${project.id}?awaiting=recording`);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "no_credit") {
        setNeedsCredit(cause.message);
        setProgress(null);
        return;
      }
      setError((cause as Error).message);
    } finally {
      setArming(false);
    }
  };

  const uploading = progress !== null;

  /**
   * The whole screen, when there is nothing to pay with.
   *
   * Replaces the form rather than sitting above it. Leaving the form up with a
   * warning over it invites somebody to fill it in and press a button that
   * cannot work, and the old arrangement did exactly that: the project was
   * created, the upload was refused, and a "Not now" left an empty project
   * that opened onto pipeline steps for work which had never started.
   *
   * There is no way past this and no attempt to look like there is. The way
   * out is the breadcrumb, which goes back to the projects that already exist.
   */
  // A beat of nothing while the answer arrives, rather than a form that may be
  // taken away. The shell still draws, so this is a blank panel and not a
  // blank page.
  if (allowed === null) {
    return <AppShell breadcrumb={<NewProjectCrumb here="Product demo" />}>{null}</AppShell>;
  }

  if (needsCredit || allowed === false) {
    return (
      <AppShell breadcrumb={<NewProjectCrumb here="Product demo" />}>
        <div className="mx-auto flex max-w-[560px] flex-col gap-4">
          <div>
            <h1 className="text-[26px] font-semibold text-ink-100">{"Add credit to make another video"}</h1>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-300">
              {needsCredit ??
                "Your free videos are used up. Adding credit is the only thing standing between you and the next one."}
            </p>
          </div>

          <div className="panel p-5">
            <p className="text-[12.5px] leading-relaxed text-ink-300">
              {"Credit does not expire and nothing renews. Top up once and it stays until you spend it."}
            </p>
            <div className="mt-4 flex items-center gap-2">
              <Link to="/settings#billing" className="btn btn-primary">
                {"Add credit"}
              </Link>
              <Link to="/dashboard" className="btn btn-ghost">
                {"Back to projects"}
              </Link>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell breadcrumb={<NewProjectCrumb here="Product demo" />}>
      <form onSubmit={submit} className="mx-auto flex max-w-[560px] flex-col gap-6">
        <div>
          <Link to="/projects/new" className="text-[12.5px] text-ink-300 hover:text-ink-100">
            ← Choose another kind
          </Link>
          <h1 className="mt-3 text-[26px] font-semibold text-ink-100">New product demo</h1>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-300">
            Record a tab, or upload a recording you already have. Either way it becomes the body of the finished piece —
            you add a title card and an end card later.
          </p>
        </div>

        {/* Recording beats uploading: it is one step instead of two. The
            extension beats recording here, because it captures what was
            clicked, which a page can never see. */}
        {pageRecording.phase.kind === "recording" ? (
          <RecordingPanel session={pageRecording.phase.session} bytes={pageRecording.phase.bytes} />
        ) : recorded ? (
          <RecordedPanel
            file={recorded.outcome.file}
            seconds={recorded.seconds}
            limited={recorded.outcome.reason === "limit"}
            disabled={uploading}
            onAgain={recordAgain}
          />
        ) : (
          <section className="surface flex flex-col gap-3 p-4">
            {/* "Record screen" is what people call this and what they look for.
                The extension, strictly, captures one tab, because click
                coordinates only line up with frame coordinates when the two
                surfaces are the same — so its own line says "the tab". */}
            <p className="text-[13px] font-semibold text-ink-100">Record screen</p>
            <div className={`grid gap-2.5 ${showExtension && inPage ? "sm:grid-cols-2" : ""}`}>
              {showExtension ? (
                <div
                  className={`flex flex-col justify-between gap-2.5 rounded-[8px] border p-3 ${
                    available ? "border-ember-500 bg-ember-500/[0.06]" : "border-[var(--edge)]"
                  }`}
                >
                  <div>
                    <p className="text-[12.5px] font-semibold text-ink-100">With the extension</p>
                    <p className="mt-0.5 text-[11.5px] leading-relaxed text-ink-300">
                      {available
                        ? "Captures the tab and every click, so the video zooms to the right thing on its own."
                        : "Captures every click, so the video zooms to the right thing on its own. Free to add to Chrome."}
                    </p>
                  </div>
                  {/* There is somewhere to send people now. A link rather than a
                      button: it leaves the page, and dressing a navigation as an
                      action is how somebody ends up wondering why nothing
                      happened. It opens in a tab so the half-made project behind
                      it survives being installed. */}
                  {available ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={arming || uploading || pageRecording.phase.kind !== "idle"}
                      onClick={() => void recordWithExtension()}
                    >
                      {arming ? "Preparing…" : "Record with extension"}
                    </button>
                  ) : (
                    <a className="btn btn-secondary" href={CHROME_STORE_URL} target="_blank" rel="noreferrer">
                      {"Add to Chrome"}
                    </a>
                  )}
                </div>
              ) : null}
              {inPage ? (
                <div
                  className={`flex flex-col justify-between gap-2.5 rounded-[8px] border p-3 ${
                    available ? "border-[var(--edge)]" : "border-ember-500 bg-ember-500/[0.06]"
                  }`}
                >
                  <div>
                    <p className="text-[12.5px] font-semibold text-ink-100">In this page</p>
                    <p className="mt-0.5 text-[11.5px] leading-relaxed text-ink-300">
                      Nothing to install. Records a tab, a window or the whole screen — clicks aren&apos;t captured, so
                      zooms are a best guess.
                    </p>
                    <label className="mt-2 flex items-center gap-2 text-[11.5px] text-ink-200">
                      <input type="checkbox" checked={withMic} onChange={(event) => setWithMic(event.target.checked)} />
                      Include my microphone
                    </label>
                  </div>
                  <button
                    type="button"
                    className={`btn ${available ? "btn-secondary" : "btn-primary"}`}
                    disabled={arming || uploading || pageRecording.phase.kind === "starting"}
                    onClick={() => void pageRecording.start(withMic)}
                  >
                    <svg
                      aria-hidden="true"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
                    </svg>
                    {pageRecording.phase.kind === "starting" ? "Choose what to share…" : "Record here"}
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        )}

        {/* A recording made here is already the chosen file; the drop zone would
            only offer to replace it, which "Record again" does more honestly. */}
        {recorded ? null : (
          <>
            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-[var(--edge)]" />
              <span className="text-[11px] text-ink-400">or upload a file</span>
              <span className="h-px flex-1 bg-[var(--edge)]" />
            </div>

            {/* The drop zone is the focal element: it is the only thing that is required. */}
            <button
              type="button"
              disabled={busyRecording}
              onClick={() => inputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                if (busyRecording) return;
                chooseFile(event.dataTransfer.files[0]);
              }}
              className={`flex flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed px-6 py-12 transition-colors duration-150 disabled:opacity-45 ${
                dragging
                  ? "border-scope-400 bg-scope-400/[0.06]"
                  : "border-[var(--edge-strong)] bg-bay-850 hover:bg-bay-800"
              }`}
            >
              {file ? (
                <>
                  <span className="text-[14px] font-semibold text-ink-100">{file.name}</span>
                  <span className="tnum text-[12px] text-ink-300">{(file.size / (1024 * 1024)).toFixed(1)} MB</span>
                  <span className="mt-1 text-[11px] text-ink-400">Click to choose a different file</span>
                </>
              ) : (
                <>
                  <span className="text-[14px] font-semibold text-ink-100">Drop a recording here</span>
                  <span className="text-[12px] text-ink-300">
                    {busyRecording ? "Not while recording" : "or click to browse"}
                  </span>
                  <span className="mt-1 text-[11px] text-ink-400">MP4, MOV, WebM or MKV · up to {MAX_MB}MB</span>
                </>
              )}
            </button>
          </>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={allowedVideoMimeTypes.join(",")}
          className="hidden"
          onChange={(event) => chooseFile(event.target.files?.[0])}
        />

        {/* After the upload: it is a question about what happens to the
            recording, and it reads as one once there is a recording to ask it
            about. The answer still decides whether a provider is needed, which
            the gaps note below says in its own words. */}
        <fieldset className="flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1 text-[11px] font-medium text-ink-300">Narration</legend>
          {(
            [
              [true, "Write it with AI", "Transcribes, trims the dead air, writes and speaks the narration."],
              [false, "Just the recording", "Title card, your footage, end card, your own audio. Add narration later."],
            ] as const
          ).map(([value, label, hint]) => (
            <label
              key={String(value)}
              className={`flex cursor-pointer items-start gap-2.5 rounded-[8px] border p-3 transition-colors ${
                narrate === value ? "border-ember-500 bg-ember-500/[0.07]" : "border-[var(--edge)] hover:bg-bay-800"
              }`}
            >
              <input
                type="radio"
                name="narrate"
                className="mt-[3px]"
                checked={narrate === value}
                disabled={uploading}
                onChange={() => setNarrate(value)}
              />
              <span>
                <span className="block text-[13px] font-medium text-ink-100">{label}</span>
                <span className="block text-[11px] leading-relaxed text-ink-400">{hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {/* Above tone and audience because it is the more basic question: those
            two say how it should sound and who it is for, this says what the
            video is doing, and a walkthrough and a release note about the same
            recording want different scripts. */}
        <Field label="What is this video?" hint="Changes how the script is written, not just how it sounds.">
          <PresetChips
            value={chosenPurpose.label}
            onChange={(label) =>
              setPurpose(videoPurposes.find((entry) => entry.label === label)?.id ?? videoPurposes[0].id)
            }
            presets={videoPurposes.map((entry) => entry.label)}
          />
          <p className="mt-1.5 text-[11px] text-ink-400">{chosenPurpose.hint}</p>
        </Field>

        <Field label="Tone" hint="Shapes how the AI rewrites your narration.">
          <PresetChips value={tone} onChange={setTone} presets={tonePresets} />
        </Field>

        <Field label="Audience" hint="Who is watching this?">
          <PresetChips value={audience} onChange={setAudience} presets={audiencePresets} />
        </Field>

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        {gaps.length > 0 ? (
          <div className="flex flex-col gap-2 rounded-[6px] border border-signal-warn/30 bg-signal-warn/10 p-3">
            <span className="text-[12px] font-medium text-signal-warn">{gapSentence(gaps)}</span>
            <p className="text-[11px] leading-relaxed text-ink-300">
              Without it the pipeline writes a placeholder script instead of transcribing your recording, and the result
              reads like a real script for someone else's video.
            </p>
            <Link to="/settings" className="self-start text-[11px] text-ink-200 underline-offset-2 hover:underline">
              Open Settings
            </Link>
          </div>
        ) : null}

        {uploading ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-[12px]">
              <span className="flex items-center gap-2 text-ink-200">
                <Spinner /> Uploading
              </span>
              <span className="tnum text-ink-300">{Math.round((progress ?? 0) * 100)}%</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-bay-700">
              <div
                className="h-full rounded-full bg-scope-400 transition-[width] duration-200"
                style={{ width: `${(progress ?? 0) * 100}%` }}
              />
            </div>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={() => navigate("/dashboard")} disabled={uploading}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!file || uploading || gaps.length > 0}
            title={gaps.length > 0 ? gapSentence(gaps) : undefined}
          >
            {uploading ? "Uploading…" : "Create project"}
          </button>
        </div>
      </form>
    </AppShell>
  );
};
